import { Injectable } from '@angular/core';
import { IProductLine } from '@models/product-line';
import { IProductVariant } from '@models/product-variant';
import { ZbPortalRedesignHelpers } from '@services/feature-flagged-features/zb-portal-redesign-helpers';
import { Capabilities } from '@shared/enums/capabilities';
import { CapabilityType } from '@shared/enums/capability-type';
import { MobileBrowserTypes } from '@shared/enums/mobile-browser-types';
import { ProductType } from '@shared/enums/product-type';
import { RoleType } from '@shared/enums/role-type';
import { VariantType } from '@shared/enums/variant-type';
import { Helpers } from '@shared/helpers';
import Path from '@shared/path/path';
import { StudentRoutes } from '@students/helpers/student-routes.enum';
import { AppConfigService } from './appconfig.service';
import { CacheService } from './cache.service';

/**
 * This service is the source of truth for building routes to different pages in the application.
 * Defining the routes here via methods allows us the ability to change page routing throughout the app more easily.
 * This can be through simply updating the string for part of a path, or even adding more complex logic that reads
 * feature flags, user roles, etc. in order to decide on the best route for the given application state.
 *
 * The naming convention is: getXYRoute(Z),
 * where X is typically the top level module that gives context, like "Teachers";
 * and Y is typically the name of the page being routed to, like "Classroom";
 * and Z is typically blank, but sometimes the route has parameters, like {@link getTeachersClassroomRoute}.
 * In the case of routes that only exist for the legacy view of the application, prefix X with "Legacy"
 * and mark the getter with a jsdoc comment: `@deprecated should be removed with legacy content`
 *
 * Methods in this service should be return the {@link Path} data type.
 */
@Injectable({
  providedIn: 'root',
})
export class AppRoutingService {

  constructor(
    private cache: CacheService,
    private appConfig: AppConfigService,
    private redesignHelpers: ZbPortalRedesignHelpers,
  ) {
  }

  /**
   * findLoginRoute will determine the most appropriate login page
   * that should be presented to the current user.
   * @returns the route most relevant to the user/system
   */
  findLoginRoute(): Path {
    // TODO split login path parts into getter methods, following existing pattern from other routes
    let route = Path.fromString('/login');

    // check for zbLoginInterop (filament requirement for superkids app)
    if (window.zbLoginInterop || false) {
      route = Path.fromString('/login/skof');
    }

    // check for presence of mobile app's WebView layer
    const unityBrowser = localStorage.getItem('myBrowser');
    if (unityBrowser && unityBrowser === MobileBrowserTypes.UNITY) {
      const returnUrl = localStorage.getItem('myBrowserReturnUrl');
      if (returnUrl) {
        localStorage.removeItem('myBrowserReturnUrl');
      }
      route = Path.fromString('/login/skof');
    } else if (this.appConfig.isHighlightsPortalUrl) {
      route = Path.fromString('/login/user');
    } else if (this.cache.lastLoginPathUsed) {
      route = Path.fromString(`/login/${this.cache.lastLoginPathUsed}`);
    }
    return route;
  }

  /**
   * Centralized location to determine the default (aka home or dashboard) route
   * for the specified RoleType.
   * @param role
   * @returns
   */
  findDefaultRouteForRole(role: RoleType): Path {
    switch (role) {

    case RoleType.DistrictAdministrator:
      return this.getDistrictsModuleRoute();

    case RoleType.SchoolAdministrator:
      return this.getSchoolsModuleRoute();

    case RoleType.Parent:
      return this.getParentModuleRoute();

    case RoleType.ApplicationAdministrator:
      return this.getAppAdminRoute();

    case RoleType.CustomerServiceAdministrator:
    case RoleType.CustomerServiceElevatedAdministrator:
      return this.getCxAdminRoute();

    case RoleType.Student:
      if (this.redesignHelpers.isZbPortal2024Active()) {
        return this.getStudentModuleRoute();
      }
      return this.getLearningModuleRoute();

    case RoleType.Teacher:
      return this.getTeachersDefaultRoute();

    default:
      return this.getLearningModuleRoute();
    }
  }

  getProductVariantRoute(
    productLine: IProductLine,
    variant: IProductVariant,
    isTeacher: boolean = true
  ): Path {
    // TODO look at breaking this down into other methods, so it doesn't require isTeacher flag param
    let route = this.getTeachersProductModule();

    if (variant.variantType === VariantType.SimplifiedCoreResources) {
      return this.getECEModuleRoute().append('weekly-planner');
    }

    route = route.append(productLine.productLineKey, variant.variantType);

    if (isTeacher) {
      if (Helpers.hasProductVariantCapability(
        variant, Capabilities.LessonPage, CapabilityType.Teacher
      )) {
        return route.append('teacher', 'lesson');
      }

      if (Helpers.hasProductVariantCapability(
        variant, Capabilities.MaterialsPage, CapabilityType.Teacher
      )) {
        return route.append('teacher', 'material');
      }

      return route;
    }

    return Helpers.hasProductVariantCapability(
      variant, Capabilities.SKOF, CapabilityType.Student
    ) ? route.append('classroom')
      : route;
  }

  // Common / Legacy Routes

  getLearningModuleRoute(): Path {
    return Path.fromString('/learning');
  }

  /** @deprecated should be removed with legacy content */
  getLegacyProductModuleRoute(): Path {
    return this.getLearningModuleRoute().append('products');
  }

  getLogoutRoute(): Path {
    return Path.fromString('/logout');
  }

  // Teachers Routes

  getTeachersModuleRoute(): Path {
    return Path.fromString('/teachers');
  }

  getTeachersProductModule(): Path {
    if (this.redesignHelpers.isZbPortal2024Active()) {
      return this.getTeachersModuleRoute().append('products');
    }
    return this.getLegacyProductModuleRoute();
  }

  getTeachersProductRoute(
    productLineKey: string,
    variantType: VariantType,
  ): Path {
    return this.getTeachersProductModule().append(
      productLineKey, variantType, 'teacher'
    );
  }

  getECEModuleRoute(): Path {
    return this.getTeachersProductModule().append('ece');
  }

  getECEWeeklyPlannerRoute(): Path {
    return this.getECEModuleRoute().append('weekly-planner');
  }

  getECEMaterialsRoute(): Path {
    return this.getECEModuleRoute().append('materials');
  }

  getTeachersDefaultRoute(): Path {
    if (this.appConfig.isHighlightsPortalUrl) {
      return this.getECEModuleRoute();
    }
    if (this.redesignHelpers.isZbPortal2024Active()) {
      return this.getTeachersModuleRoute();
    }
    return this.getLearningModuleRoute();
  }

  getTeachersClassesRoute(): Path {
    return this.getTeachersModuleRoute().append('classes');
  }

  getTeachersClassroomRoute(classroomId: string): Path {
    return this.getTeachersClassesRoute().append(classroomId);
  }

  getTeachersClassroomQuestsRoute(classroomId: string): Path {
    return this.getTeachersClassroomRoute(classroomId).append('quests');
  }

  getTeachersClassroomSettingsRoute(classroomId: string): Path {
    return this.getTeachersClassroomRoute(classroomId).append('settings');
  }

  getTeachersClassroomParentPortalRoute(classroomId: string): Path {
    return this.getTeachersClassroomRoute(classroomId).append('parent-portal');
  }

  /** @deprecated should be removed with legacy content */
  getLegacyTeachersClassesRoute(): Path {
    return this.getLearningModuleRoute().append('classes');
  }

  getTeachersNewClassRoute(): Path {
    return this.getTeachersModuleRoute().append('new-class');
  }

  getTeachersAddClassRoute(schoolId: string): Path {
    return this.getTeachersClassesRoute().append('add', schoolId);
  }

  getTeachersReportsRoute(): Path {
    return this.getTeachersModuleRoute().append('reports');
  }

  getTeachersStudentActivityReportsRoute(): Path {
    return this.getTeachersReportsRoute().append('student-activity');
  }

  getTeachersProgressReportsRoute(): Path {
    return this.getTeachersReportsRoute().append('progress-reports');
  }

  getTeachersAvailableLicensesRoute(): Path {
    return this.getTeachersProductModule().append('available-licenses');
  }

  getTeachersNoLicensesRoute(): Path {
    return this.getTeachersProductModule().append('no-licenses');
  }

  getTeachersLicensesRoute(): Path {
    return this.getTeachersModuleRoute().append('licenses');
  }

  getTeachersStudentLessonRoute(
    productLineKey: string,
    variantType: VariantType,
    productId: string,
  ): Path {
    // this does NOT build on getTeachersProductRoute, because it has a subtle different
    // so we use the getTeachersProductModule and build the similar parts here.
    return this.getTeachersProductModule()
      .append(
        productLineKey,
        variantType,
        'student-lessons',
        productId
      );
  }

  getTeachersMaterialsRoute(
    productLineKey: string,
    variantType: VariantType,
  ): Path {
    return this.getTeachersProductRoute(
      productLineKey, variantType,
    ).append('material');
  }

  getTeachersPrintAssetsRoute(
    productLineKey: string,
    variantType: VariantType,
    materialId: string,
  ): Path {
    return this.getTeachersMaterialsRoute(
      productLineKey, variantType,
    ).append(materialId, 'print-assets');
  }

  getTeachersLessonPageRoute(
    productLineKey: string,
    variantType: VariantType,
  ): Path {
    return this.getTeachersProductRoute(
      productLineKey, variantType,
    ).append('lesson');
  }

  getTeachersFiveDayPlannerRoute(
    productLineKey: string,
    variantType: VariantType,
  ): Path {
    return this.getTeachersProductRoute(
      productLineKey, variantType,
    ).append('planner');
  }

  getTeachersSubscriptionInfoRoute(): Path {
    return this.getTeachersModuleRoute().append('subscription-information');
  }

  // Student Routes

  getStudentModuleRoute(): Path {
    return Path.fromString('/student');
  }

  getStudentResourceViewerRoute(): Path {
    return this.getStudentModuleRoute().append('resource-viewer');
  }

  getStudentProductVariantRoute(
    productLine: IProductLine,
    variant: IProductVariant,
    classroomId: string
  ): Path {
    // let route = ['/student/program'];
    let route: Path = Path.fromString(StudentRoutes.PROGRAM);

    route = route.append(productLine.productLineKey, variant.variantType);

    const isSKOF = Helpers.hasProductVariantCapability(
      variant, Capabilities.SKOF, CapabilityType.Student
    );
    if (isSKOF) {
      route = route.append('classroom');
    } else {
      route = route.append(classroomId);
    }

    return route;
  }

  /** @deprecated should be removed with legacy content */
  getLegacyStudentChooseClassroomRoute(): Path {
    return this.getLearningModuleRoute().append('choose-classroom');
  }

  /** @deprecated should be removed with legacy content */
  getLegacyStudentChooseProgramRoute(): Path {
    return this.getLearningModuleRoute().append('choose-program');
  }

  /** @deprecated should be removed with legacy content */
  getLegacyStudentProgramRoute(
    productType: ProductType,
    productLineKey: string,
    variantType: VariantType
  ): Path {
    const basePath = this.getLegacyProductModuleRoute()
      .append(productLineKey, variantType);
    if (Helpers.superkidsLegacyProductTypes.includes(productType)) {
      return basePath.append('classroom');
    }
    return basePath;
  }

  // Parent Routes

  getParentModuleRoute(): Path {
    return Path.fromString('/parent');
  }

  getParentsStudentClassroomRoute(studentId: string, classroomId: string): Path {
    return this.getParentModuleRoute()
      .append('/student', studentId, 'classroom', classroomId);
  }

  getParentNoStudentsRoute(): Path {
    return this.getParentModuleRoute()
      .append('no-students');
  }

  getParentNoClassroomForStudentRoute(studentId: string): Path {
    return this.getParentModuleRoute()
      .append('student', studentId, 'no-classrooms');
  }

  getParentNoLicenseForStudentClassroomRoute(studentId: string, classroomId: string): Path {
    return this.getParentModuleRoute()
      .append('student', studentId, 'classroom', classroomId, 'no-licenses');
  }

  getParentClassUpdatesRoute(studentId: string, classroomId: string): Path {
    return this.getParentModuleRoute()
      .append('student', studentId, 'classroom', classroomId, 'overview');
  }

  getParentResourcesRoute(studentId: string, classroomId: string): Path {
    return this.getParentModuleRoute()
      .append('student', studentId, 'classroom', classroomId, 'product-resources');
  }

  getParentProgressReportsRoute(studentId: string, classroomId: string): Path {
    return this.getParentModuleRoute()
      .append('student', studentId, 'classroom', classroomId, 'progress-reports');
  }

  getParentAssignmentsRoute(studentId: string, classroomId: string): Path {
    return this.getParentModuleRoute()
      .append('student', studentId, 'classroom', classroomId, 'assignments');
  }

  getParentStudentLoginRoute(studentId: string, classroomId: string): Path {
    return this.getParentModuleRoute()
      .append('student', studentId, 'classroom', classroomId, 'login');
  }

  // Digital Admin Routes

  /** @deprecated should be removed with legacy content */
  getLegacyDigitalAdminReportsRoute(): Path {
    return this.getLearningModuleRoute().append('reports');
  }

  getDistrictsModuleRoute(): Path {
    return Path.fromString('/districts');
  }

  getSchoolsModuleRoute(): Path {
    return Path.fromString('/schools');
  }

  getSchoolRoute(schoolId: string): Path {
    return this.getSchoolsModuleRoute().append(schoolId);
  }

  getSchoolOverviewRoute(schoolId: string): Path {
    return this.getSchoolRoute(schoolId).append('overview');
  }

  /** @deprecated should be removed with legacy content */
  getLegacySchoolAdminReportsRoute(): Path {
    return this.getLearningModuleRoute().append('reports');
  }

  /** @deprecated should be removed with legacy content */
  getLegacySchoolAdminBenchmarkReportRoute(schoolId: string): Path {
    return this.getSchoolRoute(schoolId).append('admin-benchmark-report');
  }

  /** @deprecated should be removed with legacy content */
  getLegacySchoolAdminExpiredRoute(schoolId: string): Path {
    return this.getSchoolRoute(schoolId).append('expired');
  }

  // App Admin Routes

  getAdminModuleRoute(): Path {
    return Path.fromString('/admin');
  }

  getAppAdminRoute(): Path {
    return this.getAdminModuleRoute().append('app');
  }

  getCxAdminRoute(): Path {
    return this.getAdminModuleRoute().append('cx');
  }

}
